import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { useBotsFleet } from '@/api/hooks/use-bots';
import { useSystemHealth } from '@/api/hooks/use-system';
import { cn } from '@/shared/lib/utils';

type EnvMode = 'PAPER' | 'SHADOW' | 'LIVE';

interface ModeGroup {
  mode: EnvMode;
  bots: Array<{ id: string; name: string }>;
}

function deriveModeFromBots(
  bots: Array<{ id: string; execution_mode: string }>,
  runningIds: Set<string>,
  liveEnabled: boolean,
): EnvMode {
  let hasShadow = false;
  for (const bot of bots) {
    if (!runningIds.has(bot.id)) continue;
    if (liveEnabled && bot.execution_mode === 'LIVE') return 'LIVE';
    if (bot.execution_mode === 'SHADOW') hasShadow = true;
  }
  return hasShadow ? 'SHADOW' : 'PAPER';
}

const CHIP_TOKENS: Record<EnvMode, string> = {
  PAPER: 'bg-info/10 text-info-text border border-info/25',
  SHADOW: 'bg-mode-shadow text-mode-shadow-text border border-[rgba(139,92,246,0.3)]',
  LIVE: 'bg-err/10 text-err-text border border-err/25',
};

export function EnvChip() {
  const { t } = useTranslation('common');
  const { data: botsData } = useBotsFleet();
  const { data: health } = useSystemHealth();
  const liveEnabled = import.meta.env.VITE_ENABLE_LIVE_MODE === 'true';

  const { mode, groups } = useMemo(() => {
    const bots = botsData?.items ?? [];
    const runningIds = new Set(
      (health?.bots ?? []).filter((b) => b.runtime_state === 'running').map((b) => b.bot_id),
    );

    const derived = deriveModeFromBots(
      bots.map((b) => ({ id: b.id, execution_mode: b.execution_mode })),
      runningIds,
      liveEnabled,
    );

    const byMode: Record<EnvMode, Array<{ id: string; name: string }>> = {
      PAPER: [],
      SHADOW: [],
      LIVE: [],
    };
    for (const bot of bots) {
      if (!runningIds.has(bot.id)) continue;
      const m = bot.execution_mode as EnvMode;
      if (m === 'LIVE' || m === 'SHADOW' || m === 'PAPER') {
        byMode[m].push({ id: bot.id, name: bot.name });
      }
    }

    const groupOrder: EnvMode[] = ['LIVE', 'SHADOW', 'PAPER'];
    const groups: ModeGroup[] = groupOrder.map((m) => ({ mode: m, bots: byMode[m] }));
    return { mode: derived, groups };
  }, [botsData, health, liveEnabled]);

  const description = t(`envChip.${mode.toLowerCase()}` as 'envChip.paper');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Execution mode: ${mode}. ${description}`}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px]',
            'font-mono text-[10px] font-semibold uppercase tracking-[0.5px]',
            'transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50',
            CHIP_TOKENS[mode],
          )}
        >
          {mode === 'LIVE' && (
            <span
              className="h-1.5 w-1.5 rounded-full bg-err status-running-dot"
              aria-hidden="true"
            />
          )}
          {mode}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[280px] p-0">
        <div className="px-3 py-2 border-b border-line">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-55">
            {t('envChip.popoverTitle')}
          </div>
          <div className="text-[12.5px] text-text-70 mt-0.5">{description}</div>
          {!liveEnabled && (
            <div className="text-[11px] text-warn mt-1">{t('envChip.liveDisabled')}</div>
          )}
        </div>
        <ul className="py-1">
          {groups.map((g) => (
            <li key={g.mode}>
              <div className="flex items-center justify-between px-3 py-1.5 text-[11px] uppercase tracking-wide text-text-55 font-mono">
                <span>{g.mode}</span>
                <span>{g.bots.length}</span>
              </div>
              {g.bots.length > 0 ? (
                <Link
                  to={`/bots?mode=${g.mode}`}
                  className="block px-3 py-1.5 text-[12.5px] text-text-70 hover:bg-bg-hover hover:text-text"
                >
                  {t('envChip.viewBots')}
                </Link>
              ) : (
                <div className="px-3 py-1.5 text-[12.5px] text-text-40">
                  {t('envChip.noRunningBots')}
                </div>
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
